import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Chip, CircularProgress, createStyles, FormControl, FormControlLabel, FormHelperText, Grid, Icon,
    IconButton, InputAdornment, makeStyles, Paper, Radio, RadioGroup, TextField, Theme, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { MuiPickersUtilsProvider, DatePicker, KeyboardTimePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import clsx from 'clsx';

import { NewsContext } from '../NewsContext';
import { JournalistModel } from '../../../Models/JournalistModel';
import JournalistService from '../../../Services/JournalistService';
import { LocationModel } from '../../../Models/LocationModel';
import { ExpositionModel } from '../../../Models/ExpositionModel';
import { PeriodModel } from '../../../Models/PeriodModel';
import { getDateTime, numberFormat, numberToTime, timeToNumber } from '../../../shared/operators';
import { ErrorStatus } from '../../../shared/enum/ErrorStatus';
import VehicleService from '../../../Services/VehicleService';
import { VehicleModel, vehicleModelInitial } from '../../../Models/VehicleModel';
import { ClassificationModel } from '../../../Models/ClassificationModel';
import { HomeContext } from '../../Home/HomeContext';
import { EditAction } from '../../../shared/enum/EditAction';
import { CoverPresenceModel } from '../../../Models/CoverPresenceModel';
import { NewsLoadingContext } from '../NewsLoadingContext';
import { LoadingStatus } from '../enum/LoadingStatus';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { DetachedClassificationAction } from '../enum/DetachedClassificationAction';

const useStyles = makeStyles((theme: Theme) => createStyles({
    itens: {
        width: '100%',
        color: 'primary',
    },
    itemData: {
        width: '100%',
        color: 'primary',
        paddingBottom: theme.spacing(1.5),

    },
    linkColor: {
        color: '#2C64D0',
        textOverflow: 'ellipsis',
        '&:hover': {
            textDecoration: 'underline',
            cursor: 'pointer',
        },
    },
    editText: {
        textOverflow: 'ellipsis',
    },
    yellowBackground: {
        backgroundColor: theme.palette.warning.light
    },
    outsideGrid: {
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    loading: {
        minHeight: '20vh'
    },
    linksPaperError: {
        padding: theme.spacing(3),
        border: `1px solid ${theme.palette.error.main}`,
    },
    errorText: {
        paddingLeft: theme.spacing(2)
    }
}));

export default function NewsData() {
    const classes = useStyles();

    const newsContext = React.useContext(NewsContext);
    const newsLoadingContext = React.useContext(NewsLoadingContext);
    const homeContext = React.useContext(HomeContext);

    const [listJournalist, setListJournalist] = React.useState<JournalistModel[]>([]);
    const [journalistSelect, setJournalistSelect] = React.useState<JournalistModel[]>([]);
    const [errorJournalist, setErrorJournalist] = React.useState<boolean>(false);
    const [journalistSearch, setJournalistSearch] = React.useState<string>('');

    const [listVehicle, setListVehicle] = React.useState<VehicleModel[]>([]);
    const [vehicleSelect, setVehicleSelect] = React.useState<VehicleModel | null>(vehicleModelInitial);
    const [vehicleFullSelect, setVehicleFullSelect] = React.useState<VehicleModel | null>(vehicleModelInitial);

    const [titleDisabled, setTitleDisabled] = React.useState(true);
    const [clipperLinkDisabled, setClipperLinkDisabled] = React.useState(true);
    const [newsLinkDisabled, setNewsLinkDisabled] = React.useState(true);
    const [centimeterDisabled, setCentimeterDisabled] = React.useState(true);
    const [publishDateDisabled, setPublishDateDisabled] = React.useState(true);

    const [titleFocused, setTitleFocused] = React.useState(false);
    const [clipperLinkFocused, setClipperLinkFocused] = React.useState(false);
    const [newsLinkFocused, setNewsLinkFocused] = React.useState(false);
    const [centimeterFocused, setCentimeterFocused] = React.useState(false);
    const [publishDateFocused, setPublishDateFocused] = React.useState(false);

    const [titleNotDefinedError, setTitleNotDefinedError] = React.useState(false);
    const [clipperLinkNotDefinedError, setClipperLinkNotDefinedError] = React.useState(false); 
    const [noLinkDefinedError, setNoLinkDefinedError] = React.useState(false); 
    const [publishDateNotDefinedError, setPublishDateNotDefinedError] = React.useState(false); 
    const [journalistNotDefinedError, setJournalistNotDefinedError] = React.useState(false); 
    const [vehicleNotDefinedError, setVehicleNotDefinedError] = React.useState(false); 
    const [locationNotDefinedError, setLocationNotDefinedError] = React.useState(false); 
    const [coverPresenceNotDefinedError, setCoverPresenceNotDefinedError] = React.useState(false);

    const [centimeterNotDefinedError, setCentimeterNotDefinedError] = React.useState(false);
    const [durationNotDefinedError, setDurationNotDefinedError] = React.useState(false);
    const [periodNotDefinedError, setPeriodNotDefinedError] = React.useState(false);

    const [expositionNotDefinedError, setExpositionNotDefinedError] = React.useState(false);
    const [vehicleCorrectSelectNotDefinedError, setvehicleCorrectSelectNotDefinedError] = React.useState(false);

    const [vehicleMetricCorrectSelect, setVehicleMetricCorrectSelect] = React.useState<string>(''); 

    const [locationSelect, setLocationSelect] = React.useState<LocationModel | null>(null);
    const [periodSelect, setPeriodSelect] = React.useState<PeriodModel | null>(null);
    const [expositionSelect, setExpositionSelect] = React.useState<ExpositionModel[]>([]);
    const [durationDate, setDurationDate] = React.useState<Date | null>(null);
    const [coverPresenceSelect, setCoverPresenceSelect] = React.useState<CoverPresenceModel | null>(null);

    const [open, setOpen] = React.useState(false);
    const [journalistLoading, setJournalistLoading] = React.useState(false);
    
    const [classificationEditMode, setClassificationEditMode] = React.useState(false);
    const [detachedClassification, setDetachedClassification] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (homeContext.pageLocation === PageLocation.DETACHED_CLASSIFICATION)
            setDetachedClassification(true);
        else
            setDetachedClassification(false);
    }, [homeContext.pageLocation]);

    React.useEffect(() => {
        if (detachedClassification || newsContext.classification.newsId > 0 || newsContext.classification.id > 0) {
            setTitleDisabled(!detachedClassification && !!newsContext.classification.publicationTitle);
            setClipperLinkDisabled(!detachedClassification && !!newsContext.classification.clipperLink);
            setNewsLinkDisabled(!detachedClassification && !!newsContext.classification.originalLink);
            setCentimeterDisabled(!detachedClassification && !!(newsContext.classification.centimeter || newsContext.classification.centimeter == 0));
            setPublishDateDisabled(!detachedClassification && !!newsContext.classification.publicationDate);

            if (newsContext.classification.journalists.length > 0)
                setJournalistSelect(newsContext.classification.journalists);
            else if (newsContext.news.journalistSpreadsheetName) {            
                setErrorJournalist(true);
            }

            const edit = newsContext.classification.id > 0;
            fetchVehicles(edit);
            setClassificationEditMode(edit);
        }
    }, [newsContext.classification.newsId, newsContext.classification.id, detachedClassification]);   

    React.useEffect(() => {
        if (newsLoadingContext.classificationLoading == LoadingStatus.DONE && classificationEditMode)
            fetchFullEditVehicle(newsContext.classification.vehicleId);
    }, [newsLoadingContext.classificationLoading, classificationEditMode]);

    React.useEffect(() => {
        if (vehicleSelect != null && vehicleSelect.id > 0 
            && ((newsContext.vehicle.id != vehicleSelect.id)
            || vehicleFullSelect && (vehicleFullSelect.id != vehicleSelect.id))) {
            fetchFullVehicle(vehicleSelect.id);
        }
    }, [vehicleSelect]);

    React.useEffect(() => {
        if (!open) {
            setListJournalist([]);
            setJournalistLoading(false);
        }
    }, [open]);
    
    React.useEffect(() => {
        if (newsContext.errorDataStatus == ErrorStatus.RUN) {
            const error = hasError();

            if (error)
                newsContext.setErrorDataStatus(ErrorStatus.HAS_ERROR);
            else
                newsContext.setErrorDataStatus(ErrorStatus.HAS_NOT_ERROR);
        }
    }, [newsContext.errorDataStatus]);     
    
    React.useEffect(() => {
        if (listVehicle && listVehicle.length > 0 && vehicleFullSelect) {
            if (vehicleFullSelect.endOfPeriod != null) {
                const vehicle = { ...vehicleFullSelect };
                setVehicleSelect(vehicle);
            } else {
                const vehicle = listVehicle.find((item: VehicleModel) => item.id === vehicleFullSelect.id);
    
                if (vehicle){
                    setVehicleSelect(vehicle);
                }
            }
        }
    }, [listVehicle, vehicleFullSelect]);
    
    React.useEffect(() => {
        if (newsContext.detachedNewsDataAction === DetachedClassificationAction.CLEAR) {
            clearSelectedFields();
            setTitleDisabled(false);
            setPublishDateDisabled(false);
            setNewsLinkDisabled(false);
            setClipperLinkDisabled(false);
            setCentimeterDisabled(false);
            newsContext.setDetachedNewsDataAction(DetachedClassificationAction.NONE);
        }
    }, [newsContext.detachedNewsDataAction]);

    const fetchVehicles = (editMode: boolean) => {
        if (!editMode)
            homeContext.setOpenLoadingDialog(true);

        VehicleService.getVehicleByMediaClient(0, newsContext.news.clientId)
            .then((res: AxiosResponse) => { 
                setListVehicle(res.data);

                if (!editMode) {
                    if (newsContext.news.vehicleId && newsContext.news.vehicleId > 0) {
                        setVehicleSelect(res.data.find((item: VehicleModel) => item.id == newsContext.classification.vehicleId));
                        newsContext.setVehicleMetricCorrect(true);                    
                    } else if(newsContext.news.vehicleCodeMetric && newsContext.news.vehicleCodeMetric > 0) {
                        setVehicleSelect(res.data.find((item: VehicleModel) => item.codeMetric == newsContext.news.vehicleCodeMetric));
                        newsContext.setVehicleMetricCorrect(true);
                    }
                }
                
            })
            .catch((error: AxiosError) => homeContext.showError('', error))
            .finally(() => {
                if (!editMode)
                    homeContext.setOpenLoadingDialog(false);
            });
    };

    const fetchFullVehicle = (vehicleId: number) => {
        homeContext.setOpenLoadingDialog(true);
        VehicleService.vehicleForClassification(vehicleId)
            .then((res: AxiosResponse) => {
                if (res.data) {
                    const isInKeepData = newsContext.keepData && vehicleId === newsContext.classification.vehicleId;

                    if (!isInKeepData) {
                        const classification = JSON.parse(JSON.stringify(newsContext.classification));

                        if (res.data.locations.length === 1) {
                            setLocationSelect(res.data.locations[0]);
                            classification.location = res.data.locations[0];
                        }
                        else {
                            setLocationSelect(null);
                            classification.location = null;
                        }
                        
                        if (res.data.coverPresences.length === 1) {
                            setCoverPresenceSelect(res.data.coverPresences[0]);
                            classification.coverPresence = res.data.coverPresences[0];
                        }
                        else {
                            setCoverPresenceSelect(null);
                            classification.coverPresence = null;
                        }

                        if (res.data.expositions && res.data.expositions.length === 1)  
                            setExpositionSelect([res.data.expositions[0]]);                            
                        else 
                            setExpositionSelect([]); 

                        if (res.data.period && res.data.period.length === 1) {
                            setPeriodSelect(res.data.period[0]);
                            classification.period = res.data.period[0];
                        }
                        else {
                            setPeriodSelect(null);
                            classification.period = null;
                        }

                        classification.durationPriceTime = null;
                        newsContext.setClassification(classification);
                    } else  {
                        if (newsContext.classification.location)
                            setLocationSelect(newsContext.classification.location);
                        if (newsContext.classification.expositions)
                            setExpositionSelect(newsContext.classification.expositions);
                        if (newsContext.classification.period)
                            setPeriodSelect(newsContext.classification.period);
                        if (newsContext.classification.coverPresence)
                            setCoverPresenceSelect(newsContext.classification.coverPresence);
                    }

                    setVehicleFullSelect(res.data);
                    newsContext.setVehicle(res.data);
                }
            })
            .catch((error: AxiosError) => homeContext.showError('', error))
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchFullEditVehicle = (vehicleId: number) => {
        newsLoadingContext.setVehicleLoading(LoadingStatus.LOADING);

        VehicleService.vehicleForClassification(vehicleId)
            .then((res: AxiosResponse) => { 
                if (newsContext.classification.location)
                    setLocationSelect(newsContext.classification.location);
                
                if (newsContext.classification.coverPresence)
                    setCoverPresenceSelect(newsContext.classification.coverPresence);

                if (newsContext.classification.expositions)
                    setExpositionSelect(newsContext.classification.expositions);

                if (newsContext.classification.period)
                    setPeriodSelect(newsContext.classification.period);
                
                if (newsContext.classification.durationPriceTime)
                    setDurationDate(numberToTime(newsContext.classification.durationPriceTime));
                
                setVehicleFullSelect(res.data);
                newsContext.setVehicle(res.data);
                newsLoadingContext.setVehicleLoading(LoadingStatus.DONE);
            })
            .catch((error: AxiosError) => {
                homeContext.showError('', error);
                newsLoadingContext.setVehicleLoading(LoadingStatus.ERROR);
            });
    };

    const clearSelectedFields = () => {
        setJournalistSelect([]);
        setVehicleSelect(vehicleModelInitial);
        setVehicleFullSelect(vehicleModelInitial);
        setLocationSelect(null);
        setPeriodSelect(null);
        setExpositionSelect([]);
        setDurationDate(null);
        setCoverPresenceSelect(null);
    };
    
    const handleJornalistChange = (value: JournalistModel[]) => {
        const array: JournalistModel[] = [];
        
        value.forEach(journalist => {
            const found = newsContext.classification.journalists?.find(c => c.id == journalist.id);
            if (found) { 
                if (found.editAction == EditAction.DELETE)
                    array.push({...found, editAction: EditAction.NONE});
                else
                    array.push({...found});
            }
            else
                array.push({...journalist, editAction: EditAction.INSERT});
            
        });

        newsContext.classification.journalists?.forEach(journalist => {
            if (journalist) {
                const foundJournalist = array.find(c => c.id == journalist.id);   

                if (!foundJournalist && journalist.editAction != EditAction.INSERT)
                    array.push({...journalist, editAction: EditAction.DELETE});
            }
        });
        setJournalistSelect(array.filter(j => j.editAction != EditAction.DELETE));
        newsContext.setClassification({ ...newsContext.classification, journalists: array });
    };

    const handleExpositionChange = (value: ExpositionModel[]) => {
        const array: ExpositionModel[] = [];

        value.forEach(exposition => {
            const found = newsContext.classification.expositions?.find(c => c.id == exposition.id);
            if (found) { 
                if (found.editAction == EditAction.DELETE)
                    array.push({...found, editAction: EditAction.NONE});
                else
                    array.push({...found});
            }
            else
                array.push({...exposition, editAction: EditAction.INSERT});
        });

        newsContext.classification.expositions?.forEach(exposition => {
            if (exposition) {
                const foundExposition = array.find(c => c.id == exposition.id);  

                if (!foundExposition && exposition.editAction != EditAction.INSERT)
                    array.push({...exposition, editAction: EditAction.DELETE});
            }
        });   

        setExpositionSelect(array.filter(e => e.editAction != EditAction.DELETE));
        newsContext.setClassification({ ...newsContext.classification, expositions: array });
    };

    const handleChange = (prop: keyof ClassificationModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        newsContext.setClassification({ ...newsContext.classification, [prop]: event.target.value });
    };

    const handleChangeNumber = (prop: keyof ClassificationModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (numberFormat(event.target.value) > -1 || numberFormat(event.target.value) != -1){
            const number = numberFormat(event.target.value);
            newsContext.setClassification({ ...newsContext.classification, [prop]: number });
        }    
    };

    const handleVehicleMetricCorrect = (event: React.ChangeEvent<HTMLInputElement>) => {
        setVehicleMetricCorrectSelect(event.target.value);
        if((vehicleSelect != vehicleModelInitial) && (event.target as HTMLInputElement).value === 'yes') {
            newsContext.setVehicleMetricCorrect(true);
        } else { 
            newsContext.setVehicleMetricCorrect(false);
            setVehicleFullSelect(vehicleModelInitial);
            setVehicleSelect(vehicleModelInitial);
            setLocationSelect(null);
            setPeriodSelect(null);
            setExpositionSelect([]);
            setDurationDate(null);
        }
    };

    const searchJournalist = (name: string) => {
        setJournalistLoading(true);
        setJournalistSearch(name);
        JournalistService
            .getJournalistByQuery(name)
            .then((res: any) => setListJournalist(res.data))
            .catch((error: any) => homeContext.showError('', error))
            .finally(() => setJournalistLoading(false));
    };

    const handleJournalistInput = (value: string) => {
        if (value.length > 2) {
            if (value.length == 3 && journalistSearch != value)
                searchJournalist(value);
            else if (value.length > 3 && listJournalist.length == 0) {
                const name = value.substring(0, 3);
                searchJournalist(name);
            }
        } else {
            setJournalistSearch('');
            setListJournalist([]);
            setJournalistLoading(false);
        }
    };

    const hasError = () => {
        const errorTitle = !newsContext.classification.publicationTitle || newsContext.classification.publicationTitle.length > 128;
        const errorClipperLink = !detachedClassification && !newsContext.classification.clipperLink;
        const errorNeitherLink = detachedClassification && !newsContext.classification.clipperLink && !newsContext.classification.originalLink;
        const errorPublishDate = !newsContext.classification.publicationDate;
        const errorJournalist = !(newsContext.classification.journalists &&
            (newsContext.classification.journalists.filter(j => j.editAction != EditAction.DELETE).length > 0));
        const errorVehicle = !(vehicleFullSelect && (vehicleFullSelect.id > 0));
        const errorLocation = !(newsContext.classification.location && (newsContext.classification.location.id > 0));
        const errorCoverPresence = !(newsContext.classification.coverPresence && (newsContext.classification.coverPresence.id > 0));

        setTitleNotDefinedError(errorTitle);
        setClipperLinkNotDefinedError(errorClipperLink);
        setNoLinkDefinedError(errorNeitherLink);
        setPublishDateNotDefinedError(errorPublishDate);
        setJournalistNotDefinedError(errorJournalist);
        setVehicleNotDefinedError(errorVehicle);
        setLocationNotDefinedError(errorLocation);
        setCoverPresenceNotDefinedError(errorCoverPresence);

        let error = errorTitle || errorClipperLink || errorNeitherLink || errorPublishDate ||
            errorJournalist || errorVehicle || errorLocation;
        
        const vehicleHasPeriod = vehicleFullSelect && vehicleFullSelect.period 
            && vehicleFullSelect.period.some(p => p != null && p.id > 0);

        if (!vehicleHasPeriod)
        { 
            const errorCentimeter = newsContext.classification.centimeter < 0;
            setCentimeterNotDefinedError(errorCentimeter);

            error = error || errorCentimeter;
        } else {
            const errorDuration = !newsContext.classification.durationPriceTime;
            
            const errorPeriod = (!newsContext.classification.period || newsContext.classification.period.id < 1)
                && (periodSelect === null);

            setDurationNotDefinedError(errorDuration);
            setPeriodNotDefinedError(errorPeriod);

            error = error || errorDuration || errorPeriod;
        }

        if (vehicleFullSelect?.media?.name.toLowerCase() != 'rádio'){
            const errorExposition = !newsContext.classification.expositions ||
                !(newsContext.classification.expositions.filter(e => e.editAction != EditAction.DELETE).length > 0);
            setExpositionNotDefinedError(errorExposition);

            error = error || errorExposition;
        }

        if (!detachedClassification && !newsContext.classification.vehicleDictionary && !classificationEditMode){
            const errorVehicleCorrect = !(vehicleMetricCorrectSelect.length > 0);
            setvehicleCorrectSelectNotDefinedError(errorVehicleCorrect);

            error = error || errorVehicleCorrect;
        }

        return error;
    };

    const getTitle = () => {
        return (
            <Grid item xs={12}>
                <TextField
                    id="news-title"
                    value={newsContext.classification.publicationTitle}
                    onChange={handleChange('publicationTitle')}
                    label="Título da publicação"
                    variant="outlined"
                    disabled={titleDisabled}
                    className={classes.itens}
                    onBlur={() => { setTitleDisabled(true); setTitleFocused(false); }}
                    inputRef={input => titleFocused && input && input.focus()}
                    InputProps={{
                        endAdornment: (titleDisabled && 
                            <InputAdornment position="end">
                                <IconButton onClick={() => { setTitleDisabled(!titleDisabled); setTitleFocused(true); }}>
                                    <Icon>edit</Icon>
                                </IconButton>
                            </InputAdornment>)
                    }}
                    error={titleNotDefinedError &&
                        (!newsContext.classification.publicationTitle || newsContext.classification.publicationTitle.length > 128)}
                    helperText={titleNotDefinedError
                        && (!newsContext.classification.publicationTitle || newsContext.classification.publicationTitle.length > 128)
                        ? (!newsContext.classification.publicationTitle
                            ? 'Campo obrigatório'
                            : 'Título não pode ter mais de 128 caracteres')
                        : ''} />
            </Grid>
        );
    };

    const getDate = () => {
        return (
            <Grid item xs={12} sm={6} md={3}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <DatePicker
                        autoOk
                        disableFuture
                        allowKeyboardControl
                        id="news-date"
                        variant="inline"
                        value={newsContext.classification.publicationDate}
                        inputVariant="outlined"
                        label="Data de publicação"
                        onChange={(date) => newsContext.setClassification({ ...newsContext.classification, publicationDate: date })}
                        format="dd/MM/yyyy"
                        className={classes.itemData}
                        onBlur={() => { setPublishDateDisabled(true); setPublishDateFocused(false); }}
                        inputRef={input => publishDateFocused && input && input.focus()}
                        disabled={publishDateDisabled}
                        invalidDateMessage='Formato de data inválido'
                        open={publishDateFocused}
                        onClose={() => { setPublishDateDisabled(true); setPublishDateFocused(false); }}
                        InputProps={{
                            endAdornment: (( detachedClassification && !newsContext.classification.publicationDate )
                                ? <InputAdornment position="end">
                                    <IconButton onClick={() => { setPublishDateDisabled(!publishDateDisabled); setPublishDateFocused(true); }}>
                                        <Icon>date_range</Icon>
                                    </IconButton>
                                </InputAdornment>
                                : publishDateDisabled && 
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => { setPublishDateDisabled(!publishDateDisabled); setPublishDateFocused(true); }}>
                                            <Icon>edit</Icon>
                                        </IconButton>
                                    </InputAdornment>
                            )
                        }}
                        error={publishDateNotDefinedError && !newsContext.classification.publicationDate}
                        helperText={publishDateNotDefinedError && !newsContext.classification.publicationDate ? 'Campo obrigatório' : ''} />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getClipperLink = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    id="clipper-link"
                    value={newsContext.classification.clipperLink}
                    onChange={handleChange('clipperLink')}
                    label="Link da clipadora"
                    variant="outlined"
                    className={classes.itens}
                    onBlur={() => { setClipperLinkDisabled(true); setClipperLinkFocused(false); }}
                    inputRef={input => clipperLinkFocused && input && input.focus()}
                    inputProps={{
                        onClick: () => { (clipperLinkDisabled && newsContext.classification.clipperLink) && window.open(newsContext.classification.clipperLink, '_blank'); }
                    }}
                    InputProps={{
                        readOnly: clipperLinkDisabled,
                        classes: { input: (clipperLinkDisabled ? classes.linkColor : classes.editText) },
                        endAdornment: (clipperLinkDisabled &&
                            <InputAdornment position="end">
                                <IconButton onClick={() => { setClipperLinkDisabled(!clipperLinkDisabled); setClipperLinkFocused(true); }}>
                                    <Icon>edit</Icon>
                                </IconButton>
                            </InputAdornment>)
                    }}
                    error={clipperLinkNotDefinedError && !newsContext.classification.clipperLink}
                    helperText={clipperLinkNotDefinedError && !newsContext.classification.clipperLink ? 'Campo obrigatório' : ''} />
            </Grid>
        );
    };

    const getNewsLink = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    id="news-link"
                    value={newsContext.classification.originalLink}
                    onChange={handleChange('originalLink')}
                    label="Link externo"
                    variant="outlined"
                    className={classes.itens}
                    onBlur={() => { setNewsLinkDisabled(true); setNewsLinkFocused(false); }}
                    inputRef={input => newsLinkFocused && input && input.focus()}
                    inputProps={{
                        onClick: () => { (newsLinkDisabled && newsContext.classification.originalLink) && window.open(newsContext.classification.originalLink, '_blank'); }
                    }}
                    InputProps={{
                        readOnly: newsLinkDisabled,
                        classes: { input: (newsLinkDisabled ? classes.linkColor : classes.editText) },
                        endAdornment: (newsLinkDisabled &&
                            <InputAdornment position="end">
                                <IconButton onClick={() => { setNewsLinkDisabled(!newsLinkDisabled); setNewsLinkFocused(true); }}>
                                    <Icon>edit</Icon>
                                </IconButton>
                            </InputAdornment> ) 
                    }} />
            </Grid>
        );
    };

    const getLinks = () => {
        return (
            <Grid item xs={12}>
                <Paper
                    className={clsx({
                        [classes.linksPaperError]: detachedClassification && noLinkDefinedError &&
                            !newsContext.classification.clipperLink && !newsContext.classification.originalLink
                    })}
                    elevation={0}>
                    <Grid
                        container
                        direction="row"
                        spacing={3}>
                        { getClipperLink() }
                        { getNewsLink() }
                    </Grid>
                </Paper>
                { (detachedClassification && noLinkDefinedError &&
                    !newsContext.classification.clipperLink && !newsContext.classification.originalLink)
                    && (
                        <FormHelperText error className={classes.errorText}>
                            Para finalizar a inclusão da matéria é necessário preencher pelo menos um campo.
                        </FormHelperText>)
                }
            </Grid>
        );
    };

    const getVehicleMetric = () => {
        return (
            <Grid item xs={12} md={6}>
                <Autocomplete
                    id="news-vehicle-metric"
                    value={vehicleSelect}
                    onChange={(_, newValue: VehicleModel | null) => {
                        setVehicleSelect(newValue);
                        if (newValue) {
                            const classification = JSON.parse(JSON.stringify(newsContext.classification));
                            classification.vehicle = newValue;
                            newsContext.setClassification(classification);
                        }
                        else {
                            setVehicleFullSelect(null);
                        }
                    }}                
                    options={listVehicle}
                    getOptionLabel={(option: VehicleModel) => option.nomenclature || ''}
                    disabled={(!classificationEditMode 
                           && !newsContext.classification.vehicleDictionary 
                           && (!detachedClassification && (vehicleMetricCorrectSelect == ''
                              || vehicleMetricCorrectSelect == 'yes')))
                    }
                    className={classes.itens}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Veículo"
                            variant="outlined"
                            error={vehicleNotDefinedError && !(vehicleSelect && vehicleSelect.id > 0)}
                            helperText={vehicleNotDefinedError && !(vehicleSelect && vehicleSelect.id > 0) ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getVehicleMetricConfirmation = () => {     
        return (
            <>
                {(!classificationEditMode && !detachedClassification && !newsContext.classification.vehicleDictionary) &&
                    <Grid
                        container
                        direction="row"
                        spacing={3}
                        xs={12} md={6}
                        className={classes.outsideGrid}>
                        <Grid item xs={8} className={classes.yellowBackground}>
                            <Typography variant='subtitle2'>
                                O veículo preenchido automaticamente está correto?
                            </Typography>
                            <Typography variant='caption'>
                            Veículo encontrado na planilha: {newsContext.classification.vehicleNomenclature}
                            </Typography>
                        </Grid>
                        <Grid item xs={4} className={classes.yellowBackground}>
                            <FormControl component="fieldset" size='small'> 
                                <RadioGroup 
                                    row
                                    aria-label="vehicle" 
                                    value={vehicleMetricCorrectSelect} 
                                    name="correct_vehicle" 
                                    onChange={handleVehicleMetricCorrect}>
                                    <FormControlLabel
                                        value="yes"
                                        control={<Radio color='primary' size='small' />}
                                        label={<Typography variant="body2" color="textPrimary">Sim</Typography>} />
                                    <FormControlLabel
                                        value="no"
                                        control={<Radio color='primary' size='small' />}
                                        label={<Typography variant="body2" color="textPrimary">Não</Typography>} />
                                </RadioGroup>
                                {vehicleCorrectSelectNotDefinedError && (
                                    <FormHelperText error id="error-not-select">Campo obrigatório</FormHelperText>
                                )}
                            </FormControl>   
                        </Grid> 
                    </Grid>
                }
            </>
        );
    };

    const getJournalist = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    id="news-journalist"
                    className={classes.itens}
                    open={open}
                    onOpen={() => { setOpen(true); }}
                    onClose={() => { setOpen(false); }}
                    onInputChange={(_, newInputValue: string) => { handleJournalistInput(newInputValue); }}
                    value={journalistSelect}
                    onChange={(_, value: JournalistModel[]) => { handleJornalistChange(value); }}
                    getOptionSelected={(option, value) => option.name === value.name}
                    getOptionLabel={(option) => option.name}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={listJournalist}
                    filterSelectedOptions
                    loading={journalistLoading}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Jornalista/autor"
                            placeholder="Digite o nome do jornalista para buscar"
                            variant="outlined"
                            error={journalistNotDefinedError &&
                                !(journalistSelect.filter(jornalist => jornalist.editAction != EditAction.DELETE).length > 0)}
                            helperText={journalistNotDefinedError &&
                                !(journalistSelect.filter(jornalist => jornalist.editAction != EditAction.DELETE).length > 0)
                                ? 'Campo obrigatório'
                                : ''}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {journalistLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }} />

                    )}

                />
                {errorJournalist && (
                    <FormHelperText error id="error-journalist-not-found">
                        {'Não foi possível encontrar jornalista na base de dados. Jornalista presente na planilha: ' + 
                            `${newsContext.news.journalistSpreadsheetName}`}
                    </FormHelperText>
                )}
            </Grid>
        );
    };

    const handleMediaType = () => {
        if(!vehicleFullSelect?.period?.some(function (p) { return p !== null; }) )
        { 
            return getCentimeter();
        } else {
            return <>
                {getDuration()}
                {getPeriod()}
            </>;
        }
    };

    const getCentimeter = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    id="news-centimeter"
                    value={newsContext.classification.centimeter}
                    type="number"
                    onChange={handleChangeNumber('centimeter')}
                    label="Centimetragem"
                    variant="outlined"
                    disabled={centimeterDisabled}
                    className={classes.itens}
                    onBlur={() => { setCentimeterDisabled(true); setCentimeterFocused(false); }}
                    inputRef={input => centimeterFocused && input && input.focus()}
                    InputProps={{
                        endAdornment: (centimeterDisabled && 
                            <InputAdornment position="end">
                                <IconButton onClick={() => { setCentimeterDisabled(!centimeterDisabled); setCentimeterFocused(true); }}>
                                    <Icon>edit</Icon>
                                </IconButton>
                            </InputAdornment>)
                    }}
                    error={centimeterNotDefinedError && (!newsContext.classification.centimeter || newsContext.classification.centimeter < 0)}
                    helperText={centimeterNotDefinedError && (!newsContext.classification.centimeter || newsContext.classification.centimeter < 0) 
                        ? 'Campo não pode ser vazio ou negativo'
                        : ''} />
            </Grid>
        );
    };

    const getDuration = () => {
        return (
            <Grid item xs={12} sm={6} md={3}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <KeyboardTimePicker
                        ampm={false}
                        openTo="hours"
                        views={['hours', 'minutes', 'seconds']}
                        format="HH:mm:ss"
                        label="Tempo"
                        variant="inline"
                        inputVariant="outlined"
                        value={durationDate}
                        onChange={(date) => {
                            setDurationDate(date);
                            if (date)
                                newsContext.setClassification({ ...newsContext.classification, durationPriceTime: timeToNumber(date) });
                            else
                                newsContext.setClassification({ ...newsContext.classification, durationPriceTime: null });
                        }}
                        className={classes.itemData}
                        error={durationNotDefinedError && !newsContext.classification.durationPriceTime}
                        helperText={durationNotDefinedError && !newsContext.classification.durationPriceTime ? 'Campo obrigatório' : ''}
                        keyboardIcon={<Icon>timer</Icon>} />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    const getPeriod = () => {
        return (
            <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                    id="news-period"
                    value={periodSelect}
                    onChange={(_, value: PeriodModel | null) => {
                        setPeriodSelect(value);
                        newsContext.setClassification({ ...newsContext.classification, period: value });
                    }}  
                    options={vehicleFullSelect?.period ? vehicleFullSelect?.period : []}
                    getOptionLabel={(option: PeriodModel) => `${getDateTime(option.initialTime)} - ${getDateTime(option.finalTime)}`}
                    className={classes.itens}
                    fullWidth
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => 
                        <TextField
                            {...params}
                            label="Período"
                            variant="outlined"
                            error={periodNotDefinedError && !periodSelect}
                            helperText={periodNotDefinedError && !periodSelect ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getLocation = () => {
        return (
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    id="news-location"
                    value={locationSelect}
                    onChange={(_, value: LocationModel | null) => {
                        setLocationSelect(value);
                        newsContext.setClassification({ ...newsContext.classification, location: value });
                    }}  
                    options={vehicleFullSelect?.locations ? vehicleFullSelect?.locations : []}
                    getOptionLabel={(option: LocationModel) => option.name}
                    className={classes.itens}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => 
                        <TextField {...params}
                            label="Localização"
                            variant="outlined"
                            error={locationNotDefinedError && !newsContext.classification.location}
                            helperText={locationNotDefinedError && !newsContext.classification.location ? 'Campo obrigatório' : ''}
                        />}
                />
            </Grid>
        );
    };

    const getMultimediaResources = () => {
        return (
            <>
                { (vehicleFullSelect?.media?.name.toLowerCase() != 'rádio') &&
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            multiple
                            id="news-exposition" 
                            value={expositionSelect}
                            onChange={(_, value: ExpositionModel[]) => { handleExpositionChange(value); }} 
                            options={vehicleFullSelect?.expositions ? vehicleFullSelect?.expositions : []}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) => option.id === value.id}
                            filterSelectedOptions
                            className={classes.itens}
                            noOptionsText="Sem resultados"
                            loadingText="Carregando..."
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        key={index}
                                        variant="outlined"
                                        label={option.name}
                                        {...getTagProps({ index })} />
                                ))
                            }
                            renderInput={(params) => 
                                <TextField {...params} 
                                    label="Recursos multimídia" 
                                    variant="outlined"
                                    error={expositionNotDefinedError && !(expositionSelect.length > 0)}
                                    helperText={expositionNotDefinedError && !(expositionSelect.length > 0) ? 'Campo obrigatório' : ''} />
                            } />
                    </Grid> 
                }
            </>
        );
    };

    const getCoverPresence = () => (
        <>
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    id="cover-presence"
                    value={coverPresenceSelect}
                    options={vehicleFullSelect?.coverPresences ? vehicleFullSelect?.coverPresences : []}
                    onChange={(_, value: CoverPresenceModel | null) => { 
                        setCoverPresenceSelect(value);
                        newsContext.setClassification({ ...newsContext.classification, coverPresence: value });
                    }}
                    getOptionLabel={(option) => option.name}
                    fullWidth
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) => 
                        <TextField
                            {...params}
                            label="Presença na Capa"
                            variant="outlined"
                            error={coverPresenceNotDefinedError && !newsContext.classification.coverPresence}
                            helperText={coverPresenceNotDefinedError && !newsContext.classification.coverPresence ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        </>
    );

    return (
        <>
            
            <Grid>
                <Grid 
                    container 
                    spacing={3} 
                    alignItems="flex-start" >
                    { getTitle() }
                    { getDate() }
                </Grid>
                <Grid 
                    container 
                    spacing={3} 
                    alignItems="flex-start" >
                    { getLinks() }
                    { getVehicleMetric() }
                    { getVehicleMetricConfirmation() }
                    { getJournalist() }
                    { handleMediaType() }
                    { getLocation() }
                    { getMultimediaResources() }
                    { getCoverPresence() }
                </Grid>
            </Grid>
        </>
    );
}